// Files.js

/**
 * Formats a file size (in bytes) into a human readable string.
 *
 * @param {number} bytes - Raw byte count.
 * @param {object} [options]
 * @param {number} [options.decimals=1] - Number of decimal places when scaling.
 * @param {string[]} [options.units=['B','KB','MB','GB','TB']] - Custom unit labels.
 * @returns {string} A formatted size like "1.4 MB", or an empty string when invalid.
 */
export function formatFileSize(bytes, { decimals = 1, units = ['B', 'KB', 'MB', 'GB', 'TB'] } = {}) {
	if (typeof bytes !== 'number' || !isFinite(bytes) || bytes < 0) {
		return '';
	}

	if (bytes === 0) {
		return `0 ${units[0]}`;
	}

	const base = 1024;
	const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(base)), units.length - 1);
	const value = bytes / Math.pow(base, exponent);
	const fixed = value.toFixed(decimals);

	return `${parseFloat(fixed)} ${units[exponent]}`;
}

/**
 * Strict helpers for FILE node metadata (no inference/fallbacks).
 */
export function getFileNodeName(node) {
	const name = node?.data_file?.filename;
	return (typeof name === 'string' && name.trim().length > 0) ? name.trim() : '';
}

export function getFileNodeSizeBytes(node) {
	const size = node?.data_file?.size;
	return (typeof size === 'number' && Number.isFinite(size)) ? size : null;
}

/**
 * Extract a best-effort file size (bytes) from a node.
 * Supports both FILE and DOCUMENT-like schemas.
 */
export function getNodeFileSizeBytes(node) {
	const fileSize = node?.data_file?.size;
	if (typeof fileSize === 'number' && Number.isFinite(fileSize)) return fileSize;

	const documentSize = node?.data_document?.size;
	if (typeof documentSize === 'number' && Number.isFinite(documentSize)) return documentSize;

	return null;
}
